import { memo, useMemo } from 'react'

import { PartnerFormMenuItems } from 'clickNgo/entities/partner/configs'
import { ProfilingCodeType } from 'clickNgo/entities/profilingCode'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { useFormField, useGetFormDataField } from 'cng-common/features/form'
import { SideMenu } from 'shared/components/sideMenu'

import { MenuLabelWithInfo } from './components/menuLabelWithInfo/MenuLabelWithInfo'
import {
  WrappedPartnerAdditionalInfo,
  WrappedPartnerAttachments,
  WrappedPartnerBasicInfo,
  WrappedPartnerFlags,
  WrappedPartnerLegalInfo,
} from './components/wrappedComponents'
import { PartnerEditorFormKeys, PartnerEditorFormSettings } from './configs'
import { useInitPartnerEditorSetting } from './hooks/useInitPartnerEditorSetting'
import { IPartnerEditorContentProps, IPartnerEditorData } from './models'

import styles from './PartnerEditorContent.module.scss'

export const PartnerEditorContent = memo<IPartnerEditorContentProps>((props) => {
  /** ---------------------- Variables ------------------------ */

  const { isInitialized } = useInitPartnerEditorSetting(props)

  const [currentMenuItem, setCurrentMenuItem] = useGetFormDataField<PartnerFormMenuItems>(
    PartnerEditorFormKeys.Setting,
    PartnerEditorFormSettings.CurrentMenuItem
  )

  const [isInactive] = useGetFormDataField<boolean>(
    PartnerEditorFormKeys.Setting,
    PartnerEditorFormSettings.IsInactive
  )

  const { value: isLoading } = useFormField({
    defaultValue: false,
    formKey: PartnerEditorFormKeys.Setting,
    field: PartnerEditorFormSettings.IsLoading,
  })

  const [actualData] = useGetFormDataField<IPartnerEditorData>(
    PartnerEditorFormKeys.Setting,
    PartnerEditorFormSettings.ActualData
  )

  /** ---------------------- Content ------------------------ */

  const basicInfoTitle = useMemo(
    () =>
      isInitialized && (
        <MenuLabelWithInfo
          isInactive={isInactive}
          title={PartnerEditorFormKeys.BasicInfo}
          status={actualData?.partner.workflowStatus}
          modifiedValues={actualData?.partner.modifiedValues}
        />
      ),
    [
      isInitialized,
      isInactive,
      actualData?.partner.workflowStatus,
      actualData?.partner.modifiedValues,
    ]
  )

  const legalInfoTitle = useMemo(
    () =>
      isInitialized && (
        <MenuLabelWithInfo
          isInactive={isInactive}
          title={PartnerEditorFormKeys.LegalInfo}
          status={actualData?.partner.legalWorkflowStatus}
          modifiedValues={actualData?.partner.legalModifiedValues}
        />
      ),
    [
      isInitialized,
      isInactive,
      actualData?.partner.legalWorkflowStatus,
      actualData?.partner.legalModifiedValues,
    ]
  )

  if (!isInitialized) {
    return null
  }

  /** ---------------------- Render ------------------------ */

  return (
    <>
      <LoaderOverlay isVisible={isLoading} color="white" />
      <SideMenu
        onItemSelect={setCurrentMenuItem}
        selectedItem={currentMenuItem}
        classes={styles}
        color="white"
        keepMounted
      >
        <SideMenu.Item title={basicInfoTitle}>
          <WrappedPartnerBasicInfo partner={actualData.partner} />
        </SideMenu.Item>
        <SideMenu.Item title={legalInfoTitle}>
          <WrappedPartnerLegalInfo partner={actualData.partner} />
        </SideMenu.Item>
        <SideMenu.Item title={PartnerEditorFormKeys.AdditionalInfo}>
          <WrappedPartnerAdditionalInfo partner={actualData.partner} />
        </SideMenu.Item>
        <SideMenu.Item title={PartnerEditorFormKeys.Attachments}>
          <WrappedPartnerAttachments
            partner={actualData.partner}
            partnerAttachments={actualData.partnerAttachments}
          />
        </SideMenu.Item>
        <SideMenu.Separator />
        <SideMenu.Item title={PartnerEditorFormKeys.Program}>
          <WrappedPartnerFlags
            partner={actualData.partner}
            partnerFlags={actualData.partnerFlags.program}
            title={PartnerEditorFormKeys.Program}
            type={ProfilingCodeType.Program}
          />
        </SideMenu.Item>
        <SideMenu.Item title={PartnerEditorFormKeys.Accreditation}>
          <WrappedPartnerFlags
            partner={actualData.partner}
            partnerFlags={actualData.partnerFlags.accreditation}
            title={PartnerEditorFormKeys.Accreditation}
            type={ProfilingCodeType.Accreditation}
          />
        </SideMenu.Item>
      </SideMenu>
    </>
  )
})

PartnerEditorContent.displayName = 'PartnerEditorContent'
